import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { GoogleLoginButton } from '@/app/(main)/auth/_components/google-login-button';
import { useAuth } from '@/hooks/use-auth';
import { usePreferencesStore } from '@/stores/preferences/preferences-provider';
import { Loader2 } from 'lucide-react';

export function LoginPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();
  const themeMode = usePreferencesStore((s) => s.themeMode);
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;

  // Redirect to dashboard if already authenticated
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      console.log('🔓 Already authenticated, redirecting to map');
      navigate('/dashboard/map');
    }
  }, [isLoading, isAuthenticated, navigate]);

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="bg-card border rounded-3xl shadow-lg p-12 w-full max-w-md">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  // Don't render login form if already authenticated (will redirect)
  if (isAuthenticated) {
    return null;
  }

  if (!clientId) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="bg-card border rounded-3xl shadow-lg p-12 w-full max-w-md">
          <div className="space-y-2 text-center">
            <h1 className="text-3xl font-medium text-destructive">Configuration Error</h1>
            <p className="text-muted-foreground text-sm">
              Missing VITE_GOOGLE_CLIENT_ID in environment variables.
            </p>
            <p className="text-muted-foreground text-xs mt-4">
              Add it to your .env file
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="bg-card border rounded-3xl shadow-lg p-12 w-full max-w-md">
        <div className="flex flex-col items-center space-y-8">
          <div className="flex justify-center">
            <img
              src={themeMode === 'light' ? '/logo_white.svg' : '/logo.svg'}
              alt="Saturn Logo"
              className="h-24 w-auto"
            />
          </div>
          
          <div className="space-y-2 text-center w-full">
            <p className="text-muted-foreground text-sm">Sign in with your Saturn Google account</p>
          </div>
          
          <GoogleOAuthProvider clientId={clientId}>
            <GoogleLoginButton />
          </GoogleOAuthProvider>

          <div className="text-sm text-muted-foreground pt-4">
            © {new Date().getFullYear()}, Saturn Technologies.
          </div>
        </div>
      </div>
    </div>
  );
}